// @ts-nocheck
import { api, type HeadstoneDTO } from "../api/client";
import { getCurrentUser } from "../auth/session";
import { refreshPostsAccess } from "../features/posts-access";
import { onOwnedHeadstoneCreated } from "../features/pending-memorial-nav";
import { openHeadstoneStream } from "../realtime/headstoneStream";
import { dtoToLegacy } from "../utils/dto";
import { coordsFromHoleId } from "../utils/hole-id";
import { mapState } from "./state";
import { ensureHeadstoneInDOM, getHeadstoneWorldPosition } from "./holes";
import { restoreGrave } from "./grave-render";
import {
  boundsNeedFetch,
  getVisibleWorldBounds,
  isWorldPointInBounds,
  type WorldBounds,
} from "./viewport-bounds";
import {
  setPendingCemeteryLoadRetry,
  revealMapLoadingIfNeeded,
  hideMapLoadingVeil,
} from "./map-loading";

let onBackendReady: (() => void) | null = null;
let initialBootBounds: WorldBounds | null = null;
let homeLoaded = false;
let viewportFetchInFlight = false;

const HYDRATE_MAX_PER_FRAME = 16;
const HYDRATE_FRAME_BUDGET_MS = 5;
const pendingHydration = new Set<string>();
let hydrationScheduled = false;
let hydrationTimer: ReturnType<typeof setTimeout> | null = null;

export const backendReadyPromise = new Promise<void>((resolve) => {
  onBackendReady = resolve;
});

export function setInitialBootBounds(bounds: WorldBounds | null): void {
  initialBootBounds = bounds;
}

export function indexHeadstone(h: HeadstoneDTO): void {
  const legacy = dtoToLegacy(h);
  const coordKey = `${h.x},${h.y}`;
  if (coordKey !== legacy.holeId) delete mapState.gravesData[coordKey];
  mapState.gravesData[legacy.holeId] = legacy;
  if (legacy.id) mapState.graveIdToHole.set(legacy.id, legacy.holeId);
}

function shouldHydrateLegacy(holeId: string, bounds: WorldBounds): boolean {
  if (holeId.startsWith("home_")) return true;
  const [gx, gy] = coordsFromHoleId(holeId);
  const { x, y } = getHeadstoneWorldPosition(gx, gy);
  return isWorldPointInBounds(x, y, bounds);
}

export function shouldHydrateHeadstone(h: HeadstoneDTO): boolean {
  const legacy = dtoToLegacy(h);
  return shouldHydrateLegacy(legacy.holeId, getVisibleWorldBounds());
}

function hydrateLegacyByHoleId(holeId: string): void {
  const legacy = mapState.gravesData[holeId];
  if (!legacy) return;
  const [gx, gy] = coordsFromHoleId(holeId);
  const { hole } = ensureHeadstoneInDOM(gx, gy);
  const target = hole ?? mapState.holeEls[holeId] ?? null;
  if (target) restoreGrave(target, legacy);
}

export function hydrateHeadstone(h: HeadstoneDTO): void {
  indexHeadstone(h);
  const holeId = dtoToLegacy(h).holeId;
  if (mapState.isPanning) {
    enqueueHydration(holeId);
    return;
  }
  hydrateLegacyByHoleId(holeId);
}

export function applyHeadstone(h: HeadstoneDTO): void {
  indexHeadstone(h);
  if (!shouldHydrateHeadstone(h)) return;

  const holeId = dtoToLegacy(h).holeId;
  if (mapState.isPanning) {
    enqueueHydration(holeId);
    return;
  }
  hydrateLegacyByHoleId(holeId);
}

function enqueueHydration(holeId: string): void {
  pendingHydration.add(holeId);
  scheduleHydrationFlush();
}

function scheduleHydrationFlush(): void {
  if (hydrationScheduled || hydrationTimer) return;
  if (mapState.isPanning) {
    hydrationTimer = setTimeout(() => {
      hydrationTimer = null;
      scheduleHydrationFlush();
    }, 120);
    return;
  }
  hydrationScheduled = true;
  requestAnimationFrame(flushHydrationBatch);
}

function flushHydrationBatch(): void {
  hydrationScheduled = false;
  if (mapState.isPanning) {
    scheduleHydrationFlush();
    return;
  }

  let processed = 0;
  const startedAt = performance.now();

  for (const holeId of pendingHydration) {
    if (
      processed >= HYDRATE_MAX_PER_FRAME ||
      performance.now() - startedAt >= HYDRATE_FRAME_BUDGET_MS
    ) {
      scheduleHydrationFlush();
      return;
    }

    const legacy = mapState.gravesData[holeId];
    if (!legacy) {
      pendingHydration.delete(holeId);
      continue;
    }

    const existing = mapState.holeEls[holeId];
    if (existing?.classList.contains("bought") && !existing.classList.contains("hole-spot--loading")) {
      pendingHydration.delete(holeId);
      continue;
    }

    hydrateLegacyByHoleId(holeId);
    pendingHydration.delete(holeId);
    processed++;
  }
}

export function hydrateViewportGraves(): Promise<void> {
  const bounds = getVisibleWorldBounds();
  for (const [holeId] of Object.entries(mapState.gravesData)) {
    if (shouldHydrateLegacy(holeId, bounds)) enqueueHydration(holeId);
  }

  return new Promise((resolve) => {
    const wait = () => {
      if (pendingHydration.size === 0 && !hydrationScheduled) {
        resolve();
        return;
      }
      requestAnimationFrame(wait);
    };
    scheduleHydrationFlush();
    wait();
  });
}

async function fetchMapPage(
  bounds: WorldBounds,
  opts: { includeHome?: boolean; cursor?: string },
): Promise<{ items: HeadstoneDTO[]; nextCursor: string | null }> {
  const { data, error } = await api.GET("/headstones/map", {
    params: {
      query: {
        wx1: bounds.wx1,
        wy1: bounds.wy1,
        wx2: bounds.wx2,
        wy2: bounds.wy2,
        includeHome: opts.includeHome ?? false,
        limit: 200,
        cursor: opts.cursor,
      },
    },
  });
  if (error) throw error;
  return {
    items: (data?.items ?? []) as HeadstoneDTO[],
    nextCursor: data?.nextCursor ?? null,
  };
}

export async function loadCemeteryForBounds(
  bounds: WorldBounds,
  opts: { includeHome?: boolean } = {},
): Promise<void> {
  let cursor: string | undefined;
  do {
    const page = await fetchMapPage(bounds, {
      includeHome: opts.includeHome,
      cursor,
    });
    page.items.forEach(indexHeadstone);
    cursor = page.nextCursor ?? undefined;
  } while (cursor);

  mapState.loadedBboxes.push(bounds);
  if (opts.includeHome) homeLoaded = true;
}

async function runInitialCemeteryLoad(): Promise<void> {
  const bounds = initialBootBounds ?? getVisibleWorldBounds(0, 0);
  await loadCemeteryForBounds(bounds, { includeHome: true });
  await hydrateViewportGraves();

  mapState.population = Object.keys(mapState.gravesData).length;
  mapState.cemeteryLoadState = "ready";
  revealMapLoadingIfNeeded();
  console.log(
    "✅ Cimitero caricato dal backend, lapidi in memoria:",
    mapState.population,
  );
}

export async function fetchViewportIfNeeded(): Promise<void> {
  if (mapState.cemeteryLoadState !== "ready") return;
  if (mapState.isPanning) return;
  if (viewportFetchInFlight) return;

  const visible = getVisibleWorldBounds();
  if (!boundsNeedFetch(visible, mapState.loadedBboxes)) return;

  viewportFetchInFlight = true;
  try {
    await loadCemeteryForBounds(visible, { includeHome: !homeLoaded });
    await hydrateViewportGraves();
  } catch (err) {
    console.warn("Viewport headstone fetch failed", err);
  } finally {
    viewportFetchInFlight = false;
  }
}

export function removeHeadstone(id: string) {
  // DOM-free: la tomba va rimossa dai dati anche se il suo chunk non è
  // (più) montato nel DOM, altrimenti risorgerebbe alla rigenerazione.
  // Rimuove TUTTE le entry con questo id (possono coesistere duplicati
  // sotto coordKey e holeId, vedi indexHeadstone).
  const holeIds = new Set<string>();
  const indexed = mapState.graveIdToHole.get(id);
  if (indexed) holeIds.add(indexed);
  for (const [hid, data] of Object.entries(mapState.gravesData)) {
    if (data && data.id === id) holeIds.add(hid);
  }
  mapState.graveIdToHole.delete(id);

  for (const holeId of holeIds) {
    delete mapState.gravesData[holeId];
    pendingHydration.delete(holeId);

    const el = mapState.holeEls[holeId];
    if (el) {
      el.classList.remove("bought", "hole-spot--loading");
      el.innerHTML = "";
      delete (el as any).__graveData;
    }
  }
}

export function initBackendSync(): void {
  mapState.cemeteryLoadState = "loading";

  const startLoad = () => {
    setPendingCemeteryLoadRetry(null);
    mapState.cemeteryLoadState = "loading";
    hideMapLoadingVeil();
    runInitialCemeteryLoad()
      .catch((err) => {
        console.error("Errore caricamento cimitero", err);
        mapState.cemeteryLoadState = "error";
        setPendingCemeteryLoadRetry(() => {
          mapState.loadedBboxes = [];
          homeLoaded = false;
          startLoad();
        });
        revealMapLoadingIfNeeded();
      })
      .finally(() => {
        onBackendReady?.();
      });
  };

  startLoad();

  openHeadstoneStream({
    onOpen: () => console.log("📡 SSE /headstones/stream aperto"),
    onCreated: (h) => {
      mapState.population++;
      applyHeadstone(h);
      const user = getCurrentUser();
      if (user && h.owner?.id === user.id) {
        refreshPostsAccess().catch(() => null);
        onOwnedHeadstoneCreated(h);
      }
    },
    onUpdated: (h) => applyHeadstone(h),
    onDeleted: (id) => {
      mapState.population = Math.max(0, mapState.population - 1);
      removeHeadstone(id);
    },
    onError: (e) => console.warn("SSE error", e),
  });
}

export async function waitForCemeteryReady(): Promise<void> {
  if (mapState.cemeteryLoadState === "ready") return;
  await backendReadyPromise;
}
